// C1 - Ünite 5: Professional Communication Mastery
// Hedef: De-escalation, diplomatic expressions, cross-cultural communication

export const C1_UNIT_5 = {
  'C1-U5-L1': {
    id: 'C1-U5-L1',
    title: 'De-escalation Language',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 De-escalation Teknikleri',
          explanation: 'Gergin durumları sakinleştirmek ve çatışmayı önlemek için kullanılan dil teknikleri.',
          table: {
            headers: ['Teknik', 'Örnek', 'Kullanım'],
            rows: [
              ['Empati Gösterme', 'I understand how frustrating this must be.', 'Yolcunun duygusunu onaylama'],
              ['Aktif Dinleme', 'Let me make sure I understand correctly...', 'Anlaşıldığını gösterme'],
              ['Sakin Ses Tonu', 'I\'m here to help you.', 'Güven verme'],
              ['Çözüm Odaklı', 'Let me see what I can do for you.', 'Çözüm arama'],
              ['Sınırları Belirleme', 'I need to ask you to lower your voice, please.', 'Kibar ama kararlı']
            ]
          }
        },
        {
          type: 'vocabulary',
          title: '✈️ Sakinleştirici İfadeler',
          words: [
            { en: 'I completely understand your concern.', tr: 'Endişenizi tamamen anlıyorum.' },
            { en: 'I apologize for any inconvenience caused.', tr: 'Yaşanan her türlü rahatsızlık için özür dilerim.' },
            { en: 'Let\'s work together to find a solution.', tr: 'Birlikte bir çözüm bulalım.' },
            { en: 'I\'m doing everything in my power to assist you.', tr: 'Size yardım etmek için elimden gelen her şeyi yapıyorum.' },
            { en: 'Your feedback is valuable to us.', tr: 'Geri bildiriminiz bizim için değerli.' }
          ]
        },
        {
          type: 'tip',
          content: '💡 Asla "calm down" (sakin ol) demeyin! Bu durumu daha da gerginleştirir. Bunun yerine empati gösterin.'
        }
      ],
      exercises: [
        {
          type: 'swipeVocabulary',
          title: '🎴 De-escalation İfadeleri',
          cards: [
            { front: 'I understand how you feel.', back: 'Nasıl hissettiğinizi anlıyorum.', example: 'Empati gösterme' },
            { front: 'Let me help you with that.', back: 'Bununla ilgili size yardım edeyim.', example: 'Çözüm odaklı' },
            { front: 'I apologize for the inconvenience.', back: 'Rahatsızlık için özür dilerim.', example: 'Özür' },
            { front: 'What can I do to make this right?', back: 'Bunu düzeltmek için ne yapabilirim?', example: 'Çözüm sunma' },
            { front: 'I hear your concerns.', back: 'Endişelerinizi duyuyorum.', example: 'Aktif dinleme' },
            { front: 'Thank you for your patience.', back: 'Sabrınız için teşekkür ederim.', example: 'Takdir' }
          ]
        },
        {
          type: 'multipleChoice',
          question: 'An angry passenger says "This is unacceptable!" The best response is:',
          options: [
            'Calm down, please.',
            'It\'s not my fault.',
            'I understand this is frustrating. Let me see what I can do.',
            'There\'s nothing I can do.'
          ],
          correctIndex: 2,
          explanation: 'Empati + Çözüm odaklı yaklaşım en etkili yöntemdir.'
        }
      ]
    }
  },

  'C1-U5-L2': {
    id: 'C1-U5-L2',
    title: 'Diplomatic Expressions',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Diplomatik İfadeler',
          explanation: 'Olumsuz haberleri nazikçe iletmek ve hassas durumları yönetmek için.',
          table: {
            headers: ['Direkt', 'Diplomatik', 'Kullanım'],
            rows: [
              ['No', 'I\'m afraid that won\'t be possible.', 'Red'],
              ['You\'re wrong', 'I see it slightly differently.', 'Düzeltme'],
              ['Wait', 'Would you mind waiting a moment?', 'Rica'],
              ['I don\'t know', 'Let me find out for you.', 'Bilmediğinde'],
              ['That\'s your problem', 'I understand the situation. Let\'s see...', 'Çözüm odaklı'],
              ['You can\'t', 'Unfortunately, regulations don\'t allow...', 'Yasak']
            ]
          }
        },
        {
          type: 'example',
          title: '✈️ Kabin Durumları',
          items: [
            { en: 'I\'m afraid we\'ve run out of the chicken option.', tr: 'Korkarım ki tavuk seçeneğimiz bitti.' },
            { en: 'Would you mind moving to another seat?', tr: 'Başka bir koltuğa geçer misiniz?' },
            { en: 'I\'ll need to check with the captain on that.', tr: 'Bunu kaptan ile kontrol etmem gerekecek.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'A passenger wants to change seats, but all are taken. The diplomatic response is:',
          options: [
            'No, you can\'t.',
            'The flight is full.',
            'I\'m afraid all seats are currently occupied, but I\'ll let you know if anything becomes available.',
            'Why do you want to change?'
          ],
          correctIndex: 2,
          explanation: 'Açıklama + Alternatif sunma diplomatik yaklaşımdır.'
        }
      ]
    }
  },

  'C1-U5-L3': {
    id: 'C1-U5-L3',
    title: 'Formal vs Informal Register',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Resmi vs Günlük Dil',
          explanation: 'Duruma göre dil seviyesini ayarlama. Anonslarda resmi, bireysel iletişimde daha sıcak.',
          table: {
            headers: ['Informal', 'Formal', 'Durum'],
            rows: [
              ['Hi!', 'Good evening, ladies and gentlemen.', 'Karşılama'],
              ['Can I help?', 'How may I assist you?', 'Yardım teklifi'],
              ['Sure!', 'Certainly, sir/madam.', 'Onay'],
              ['Wait a sec.', 'One moment, please.', 'Bekleme'],
              ['Sorry about that.', 'Please accept our apologies.', 'Özür'],
              ['See ya!', 'Thank you for flying with us.', 'Veda']
            ]
          }
        },
        {
          type: 'tip',
          content: '💡 PA anonsları = Resmi | Bireysel iletişim = Yarı resmi, sıcak | Acil durum = Net ve kısa'
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'Which is appropriate for a PA announcement?',
          options: [
            'Hey everyone, we\'re landing soon.',
            'Ladies and gentlemen, we will shortly begin our descent.',
            'We\'re gonna land in a bit.',
            'Landing time, folks!'
          ],
          correctIndex: 1,
          explanation: 'PA anonsları resmi ve profesyonel olmalıdır.'
        }
      ]
    }
  },

  'C1-U5-L4': {
    id: 'C1-U5-L4',
    title: 'Cross-Cultural Communication',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '🌍 Kültürlerarası İletişim',
          explanation: 'Farklı kültürlerden yolcularla etkili iletişim için farkındalık.',
          table: {
            headers: ['Kültür', 'Özellik', 'İletişim İpucu'],
            rows: [
              ['Doğu Asya', 'Dolaylı iletişim, yüz kaybından kaçınma', 'Nazik ve dolaylı ol'],
              ['Orta Doğu', 'Misafirperverlik önemli', 'Sıcak ve samimi ol'],
              ['Kuzey Avrupa', 'Direkt ve zaman odaklı', 'Net ve öz ol'],
              ['Hint Alt Kıtası', 'Saygı ve hiyerarşi', 'Resmi hitap kullan'],
              ['Latin Amerika', 'Kişisel ilişki önemli', 'Sıcak ve dostane ol']
            ]
          }
        },
        {
          type: 'vocabulary',
          title: '✈️ Kültürel Farkındalık',
          words: [
            { en: 'In some cultures, direct eye contact may be considered rude.', tr: 'Bazı kültürlerde doğrudan göz teması kaba sayılabilir.' },
            { en: 'Personal space varies by culture.', tr: 'Kişisel alan kültüre göre değişir.' },
            { en: 'Dietary restrictions may be religious or cultural.', tr: 'Diyet kısıtlamaları dini veya kültürel olabilir.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'A passenger from Japan seems hesitant to ask for help. You should:',
          options: [
            'Ignore them until they ask.',
            'Proactively offer assistance in a gentle, non-intrusive way.',
            'Ask directly "What do you want?"',
            'Wait for eye contact.'
          ],
          correctIndex: 1,
          explanation: 'Japon kültüründe dolaylı iletişim önemlidir, proaktif ama nazik yaklaşım idealdir.'
        }
      ]
    }
  },

  'C1-U5-L5': {
    id: 'C1-U5-L5',
    title: 'Uygulama: Zor Yolcu Yönetimi',
    type: 'practice',
    content: {
      sections: [
        {
          type: 'scenario',
          title: '✈️ Senaryo: Öfkeli Yolcu',
          dialogues: [
            {
              speaker: 'Yolcu',
              text: 'This is ridiculous! I\'ve been waiting for my meal for 30 minutes!',
              translation: 'Bu saçmalık! 30 dakikadır yemeğimi bekliyorum!'
            },
            {
              speaker: 'Kabin Memuru',
              text: 'I completely understand your frustration, sir. I apologize for the delay. Let me personally ensure your meal is brought right away.',
              translation: 'Hayal kırıklığınızı tamamen anlıyorum, efendim. Gecikme için özür dilerim. Yemeğinizin hemen getirilmesini bizzat sağlayayım.'
            },
            {
              speaker: 'Yolcu',
              text: 'Well... alright. Thank you.',
              translation: 'Peki... tamam. Teşekkürler.'
            },
            {
              speaker: 'Kabin Memuru',
              text: 'Thank you for your patience. Is there anything else I can get you while you wait?',
              translation: 'Sabrınız için teşekkür ederim. Beklerken size getirebileceğim başka bir şey var mı?'
            }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'In the scenario, the cabin crew used which technique?',
          options: [
            'Ignoring the complaint',
            'Empathy + Apology + Solution',
            'Blaming the kitchen',
            'Arguing with the passenger'
          ],
          correctIndex: 1,
          explanation: 'Empati gösterme + Özür + Çözüm sunma en etkili de-escalation tekniğidir.'
        }
      ]
    }
  },

  'C1-U5-EXAM': {
    id: 'C1-U5-EXAM',
    title: '📝 Ünite 5 Sınavı',
    type: 'exam',
    content: {
      timeLimit: 25,
      passingScore: 80,
      questions: [
        {
          type: 'multipleChoice',
          question: 'A passenger is angry. The WORST response is:',
          options: [
            'I understand your frustration.',
            'Calm down, please.',
            'Let me help you with that.',
            'I apologize for the inconvenience.'
          ],
          correctIndex: 1
        },
        {
          type: 'multipleChoice',
          question: 'The diplomatic way to say "No" is:',
          options: [
            'No way.',
            'I\'m afraid that won\'t be possible.',
            'Absolutely not.',
            'You can\'t do that.'
          ],
          correctIndex: 1
        },
        {
          type: 'multipleChoice',
          question: 'For PA announcements, which register is appropriate?',
          options: ['Informal', 'Formal', 'Slang', 'Casual'],
          correctIndex: 1
        }
      ]
    }
  }
};

export default C1_UNIT_5;
